import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonResponsiveWrapper, Inscription, Preloader, useResponsive } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { useSnackbar } from '@/modules/core/hooks';

import { Patient, PatientPayload, useCreatePatientQuery } from '../usePatients.query';

import PatientForm from './PatientForm';
import usePatientStore from './usePatient.store';

interface Props {
  patientName?: string;
  submitButtonText?: string;
  patients?: Patient[];
  onCancel: () => void;
  onSuccess?: (createdPatient?: PatientPayload) => void;
  onError?: (error: unknown) => boolean;
}

const AddPatientForm: React.FC<Props> = ({
  patientName,
  submitButtonText,
  patients,
  onCancel,
  onSuccess,
  onError,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const [isError, setIsError] = useState(false);
  const { save, isLoading } = useCreatePatientQuery(onSuccess);
  const { handleReset } = usePatientStore();

  const isPatientAlreadyExist = useMemo(
    () => patients?.find((patient) => patient?.fullName === patientName),
    [patients, patientName]
  );

  const handleSubmit = useCallback(
    async (value: Patient) => {
      try {
        setIsError(false);
        const patient = await save(value);
        const patientFullName = `${patient?.first_name} ${patient?.last_name}`;
        if (patientFullName) {
          handleAddPermanentSnackbar({
            text: `${patientFullName} ${t('added')}.`,
            closeIcon: true,
          });
        }
      } catch (e) {
        setIsError(!onError?.(e));
      }
    },
    [save, handleAddPermanentSnackbar, t, onError]
  );

  useEffect(() => {
    handleReset();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  return (
    <>
      <PatientForm
        {...(isPatientAlreadyExist ? {} : { patientName })}
        onSubmit={handleSubmit}
        footer={
          <Box
            fill={isMobile}
            direction={isMobile ? 'column' : 'row'}
            align="end"
            pad={{ top: 'spacing24' }}
            margin={{ top: 'auto' }}
          >
            {isError && (
              <Inscription alignSelf="end" color="danger" margin={{ bottom: 'spacing24' }}>
                {t('Something goes wrong!')}
              </Inscription>
            )}
            {isMobile ? (
              <Box
                fill
                direction="row"
                justify="end"
                gap="spacing12"
                pad={{ bottom: 'spacing12' }}
                align={isMobile ? 'end' : 'center'}
              >
                <div style={{ flex: 1 }}>
                  <AppButton type="button" onClick={onCancel} buttonType="secondary" fill testId="cancel">
                    {t('Cancel')}
                  </AppButton>
                </div>
                <div style={{ flex: 1 }}>
                  <AppButton type="submit" testId="submit" fill>
                    {isLoading ? <Preloader color="white" /> : t(submitButtonText || 'Save Dependent')}
                  </AppButton>
                </div>
              </Box>
            ) : (
              <ButtonResponsiveWrapper fill justify="end" align="center">
                <AppButton
                  type="button"
                  onClick={onCancel}
                  buttonType="secondary"
                  width="control"
                  testId="cancel"
                >
                  {t('Cancel')}
                </AppButton>
                <AppButton type="submit" width="control" testId="submit">
                  {isLoading ? <Preloader color="white" /> : t(submitButtonText || 'Save Dependent')}
                </AppButton>
              </ButtonResponsiveWrapper>
            )}
          </Box>
        }
      />
    </>
  );
};

export default AddPatientForm;
